<script setup lang="ts">
/**
 * Shell for the signed-out pages: sign in, register, and the password and
 * verification flows.
 *
 * It imposes no width on its slot, because /login lays out as two columns
 * while the smaller pages centre a narrow card — each view owns its own
 * measure rather than fighting a wrapper.
 *
 * The language switcher is here rather than only on the landing page because
 * these are exactly the pages somebody reaches without an account: there is
 * no stored language preference to read yet, so this is their only way to say
 * what they read before they have one. Somebody who cannot read the form
 * cannot fill it in.
 */
import { RouterLink } from 'vue-router';

import { SUPPORTED_LOCALES, chooseLocale, locale, useI18n } from '../i18n/index.ts';

const { t } = useI18n();
const year = new Date().getFullYear();

const LOCALE_LABELS: Record<string, string> = { th: 'ไทย', en: 'English' };
</script>

<template>
  <div class="flex min-h-full flex-col bg-surface text-body-md text-on-surface">
    <header class="tv-glass z-20 border-x-0 border-t-0">
      <div class="mx-auto flex h-16 max-w-7xl items-center justify-between gap-space-md px-gutter">
        <RouterLink to="/" class="flex items-center gap-space-sm">
          <img src="/logo.png" alt="" class="h-8 w-8" />
          <span class="text-headline-sm font-bold tracking-tight">TalkVibe</span>
        </RouterLink>

        <div class="inline-flex items-center rounded-full bg-surface-container-low p-space-xs">
          <button
            v-for="code in SUPPORTED_LOCALES"
            :key="code"
            type="button"
            class="rounded-full px-space-sm py-1 text-label-sm transition-colors"
            :class="
              locale === code
                ? 'bg-surface-container-lowest text-primary shadow-sm'
                : 'text-on-surface-variant hover:text-on-surface'
            "
            :aria-pressed="locale === code"
            @click="chooseLocale(code)"
          >
            {{ LOCALE_LABELS[code] ?? code.toUpperCase() }}
          </button>
        </div>
      </div>
    </header>

    <main class="flex flex-1 flex-col">
      <slot />
    </main>

    <footer class="bg-surface-container-lowest py-space-lg">
      <div
        class="mx-auto flex max-w-7xl flex-col items-center justify-between gap-space-md px-gutter sm:flex-row"
      >
        <nav class="flex flex-wrap items-center justify-center gap-space-lg" :aria-label="t('landing.footer.label')">
          <RouterLink
            to="/terms"
            class="text-body-sm text-on-surface-variant transition-colors hover:text-primary"
          >
            {{ t('legal.terms') }}
          </RouterLink>
          <RouterLink
            to="/privacy"
            class="text-body-sm text-on-surface-variant transition-colors hover:text-primary"
          >
            {{ t('legal.privacy') }}
          </RouterLink>
          <RouterLink
            to="/forgot-password"
            class="text-body-sm text-on-surface-variant transition-colors hover:text-primary"
          >
            {{ t('auth.forgotPassword') }}
          </RouterLink>
        </nav>
        <p class="text-body-sm text-on-surface-variant">
          © {{ year }} TalkVibe. {{ t('landing.footer.rights') }}
        </p>
      </div>
    </footer>
  </div>
</template>
